// Libraries
import React from 'react';
import PropTypes from 'prop-types';
import config from 'config';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import {browserHistory} from 'react-router';
import {Helmet} from 'react-helmet';

const _ = require('lodash');

// Modules
import CatalogCards from 'components/catalog/cards/catalogCards';
import * as mixpanelTracker from 'app-lib/mixpanel/mixpanelTracker';
import CarouselFeatured from 'components/carousels/carousel-featured';
import Groups from 'components/catalog/group/groups';
import { OverlaySpinner } from 'components/SharedComponents/overlaySpinner';
import { NoFeaturedEvent } from 'components/SharedComponents/featuredEmpty';
import { MessageBanner } from 'components/SharedComponents/messageBanner';
import ThemeSelector from 'components/SharedComponents/themeSelector';
import * as IntercomMessenger from 'app-lib/intercom/intercom';
import ChannelInfo from 'components/catalog/info/channel-info';
import CreateGroupPopover from 'components/catalog/group/create-group-popover';
import TOSBanner from 'components/SharedComponents/banner/terms-of-service-banner';


// Helpers
import * as contentsHelper from 'app-lib/helpers/contents-helper';
import { appLogger } from 'app-lib/helpers/logging';
import { filePickerHelper } from 'app-lib/helpers/filepicker';
import * as groupsHelper from 'app-lib/helpers/groups-helper';
import PageTracker from 'app-lib/helpers/page-tracker';
import Page from 'src/model/page';

// REST
import * as HeaderActions from '@getgo/webinar.com-frontend-redux/lib/shared/actions/header';
import * as FooterActions from '@getgo/webinar.com-frontend-redux/lib/shared/actions/footer';
import * as contentsActions from '@getgo/webinar.com-frontend-redux/lib/shared/actions/contents';
import * as channelsActions from '@getgo/webinar.com-frontend-redux/lib/shared/actions/channel-profile';
import * as detectBrowser from 'app-lib/helpers/browser-detect-helper';
import * as UserSettingActions from '@getgo/webinar.com-frontend-redux/lib/shared/actions/user-setting';
import * as NotificationActions from '@getgo/webinar.com-frontend-redux/lib/shared/actions/notification';

// Strings
const classPrefix = 'my-catalog-page';
const logger = appLogger.create('catalog');

export class Catalog extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            showShareTooltip: false,
            characterEntered: 0,
            showChannelLogoTooltip: false
        };
    }

    componentDidMount() {
        PageTracker.lastPage = Page.CHANNEL;
        detectBrowser.checkNameAndVersion();
        if (this.props.channel) {
            mixpanelTracker.setIsChannelPublic(!this.props.channel.isLimitedAudience);
            mixpanelTracker.track(`${mixpanelTracker.channelPage}-${mixpanelTracker.pageVisitEvent}`);
            this.setHeader(this.props.channel);
            this.updateFooter();
        }
        if (this.props.isChannelOwner) {
            this.props.getUserSettings();
        }
        (this.props.channelKey && !this.props.channelKey.includes('edit')) ? this.getFeaturedContent() : null;
    }


    componentDidUpdate(prevProps, prevState) {
        if (this.props.isChannelOwner && (prevProps.isChannelOwner !== this.props.isChannelOwner)) {
            this.setHeader(this.props.channel);
            this.props.getUserSettings();
            this.updateChannelPrivacyToIntercom();
        }

        if (this.props.channelKey && !this.props.channelKey.includes('edit') && (prevProps.channelKey !== this.props.channelKey)) {
            this.getFeaturedContent();
        }

        // update intercom with contentsCount
        if (this.props.contents && (prevProps.contents.length !== this.props.contents.length) && !this.props.contentsFetching) {
            if (this.props.isChannelOwner && this.props.identity && this.props.identity.me) {
                IntercomMessenger.updateContentCount(this.props.contents.length, this.props.identity.me.userName);
            }
        }

        if (this.props.channel && prevProps.channel &&
             this.props.channel.isLimitedAudience !== prevProps.channel.isLimitedAudience) {
            mixpanelTracker.setIsChannelPublic(!this.props.channel.isLimitedAudience);
            this.updateChannelPrivacyToIntercom();
        }
        if (this.props.channel && !prevProps.channel) {
            mixpanelTracker.setIsChannelPublic(!this.props.channel.isLimitedAudience);
            mixpanelTracker.track(`${mixpanelTracker.channelPage}-${mixpanelTracker.pageVisitEvent}`);
            this.updateChannelPrivacyToIntercom();
        }

        if (this.props.channel && !_.isEqual(prevProps.channel, this.props.channel)) {
            this.setHeader(this.props.channel);
            this.updateFooter();
            const isChannelKeyDifferent = ((!prevProps.channel) || !(prevProps.channel.channelKey) || (this.props.channel.channelKey !== prevProps.channel.channelKey));
            if (this.props.channel && this.props.channel.defaultGroupKey && isChannelKeyDifferent) {
                this.props.getGroupData(this.props.channelKey, groupsHelper.getAllGroupKeys(this.props.channel).toString());
            }
        } else if (this.props.channel && _.isEqual(prevProps.channel, this.props.channel) && !this.props.groupData && (this.props.channelProfileFetching < 1)) {
            this.props.getGroupData(this.props.channelKey, groupsHelper.getAllGroupKeys(this.props.channel).toString());
        }

        if (this.state.characterEntered !== prevState.characterEntered) {
            this.setHeader(this.props.channel);
        }
    }

    componentWillUnmount() {
        this.props.initHeader();
    }

    updateChannelPrivacyToIntercom() {
        const {isChannelOwner, identity, channel} = this.props;
        if (isChannelOwner && identity && identity.me && channel) {
            IntercomMessenger.updateChannelPrivacy(channel.isLimitedAudience, identity.me.userName);
        }
    }

    getFeaturedContent() {
        this.props.getFeaturedContentList(this.props.channelKey);
    }

    mountInternalErrorPage(errorMsg, error) {
        this.logAttributes(errorMsg, error);
        browserHistory.push('/internalError');
    }

    logAttributes(errorMsg, error) {
        logger.error(errorMsg, { error });
    }

    popUrlLast() {
        const allUrlElements = window.location.href.split('/');
        const urlElements = allUrlElements.filter(Boolean);
        return urlElements.pop();
    }
    setTooltipState(cssSelector, localStorageId, prop) {
        // first fadeoput the tooltip and then remove it from the DOM
        const elem = document.querySelector(cssSelector);
        elem.classList.add('fadeOut');
        const self = this;
        try {
            window.setTimeout(function () {
                try {
                    localStorage.setItem(localStorageId, true);
                } catch (e) {
                    console.log('error', e);
                }
                self.setState(prop);
                elem.classList.remove('fadeIn');
            }, 1000);
        } catch (e) {
            console.log('error', e);
        }
    }

    openFilePicker() {
        const self = this;
        const filePickerOptions = {
            fileType: 'banners',
            mimetype: 'image/*',
            cropRatio: 6 / 1,
            imageMin: [null, 240],
            customText: config.filepicker.banner.customText,
            customCss: config.filepicker.banner.customCss
        };

        filePickerHelper.pickAndStore(filePickerOptions, (bannerUrl) => {
            self.props.putChannelBanner(self.props.channel.channelKey, bannerUrl);
            mixpanelTracker.track(`${mixpanelTracker.channelPage}-${mixpanelTracker.bannerImageUploaded}`);
        });
    }
    resetBannerImage() {
        this.props.putChannelBanner(this.props.channel.channelKey, false);
    }

    bannerChangeHandler() {
        if (this.props.channel && !this.props.channel.channelBanner && !this.props.channel.channelBannerCdn) {
            mixpanelTracker.track(`${mixpanelTracker.channelPage}-${mixpanelTracker.bannerUploadClicked}`);
            this.openFilePicker();
        } else {
            mixpanelTracker.track(`${mixpanelTracker.channelPage}-${mixpanelTracker.bannerRemoveClicked}`);
            this.resetBannerImage();
        }
    }

    testRegEx(re, str) {
        if (re.test(str)) {
            return true;
        }
        return false;
    }

    // -------------------------------------------------------------------------------------- NAVIGATION ELEMENTS --------------------------------------------------------------------------------------
    setHeader(channel) {
        const self = this;

        if (channel) {
            self.props.initHeader({
                channelTheme: `${this.props.channel.channelTheme || 'default'}`,
                content: [this.navThemeSelector()],
                imgsrc: channel.channelBanner && channel.channelBannerCdn ? channel.channelBannerCdn : null,
                showSearch: !channel.isLimitedAudience,
                searchBarColor: 'black'
            });
        }
    }

    navThemeSelector() {
        const { channel, isChannelOwner } = this.props;
        return (
            <ThemeSelector
              initialColor={channel && channel.channelTheme && channel.channelTheme !== 'default' ? channel.channelTheme : 'default'}
              clickTheme={this.colorSelected.bind(this)}
              clickBanner={this.bannerChangeHandler.bind(this)}
              banner={channel && channel.channelBanner && channel.channelBannerCdn ? channel.channelBannerCdn : null}
              show={isChannelOwner}
            />
        );
    }

    colorSelected(color, e) {
        const self = this;
        mixpanelTracker.track(`${mixpanelTracker.channelPage}-${mixpanelTracker.colorSelected}`, {color});
        self.setState({themeName: color});
        self.props.putChannelTheme(self.props.channel.channelKey, color);
    }

    updateFooter() {
        const {t} = this.props;
        const styles = {
            backgroundColor: `${this.props.channel.channelTheme && this.props.channel.channelTheme !== 'default' ? this.props.channel.channelTheme : ''}`,
            color: `${this.props.channel.channelThemeTextColor && this.props.channel.channelTheme !== 'default' ? this.props.channel.channelThemeTextColor : ''}`
        };

        const rightFooter = [
            <span id="terms&conditions" key="terms&conditions"><a href={`${config.company.termsOfService}`} style={styles} target="_blank" rel="noopener">{t('channel.footer.tnc')}</a></span>,
            <span key="privacy"><a href={`${config.company.privacyPolicy}`} target="_blank" rel="noopener" style={styles} >{t('channel.footer.privacy')}</a></span>,
            <span key="rights" style={styles} >&copy; {new Date().getFullYear()} {t('channel.footer.company')} {t('channel.footer.copyright')}</span>
        ];

        this.props.setClassNameFooter({
            classes: 'generic-page-footer',
            styles
        });

        this.props.setRightFooter(rightFooter);
    }
    // ------------------------------------------- ERRORS ------------------------------------------------
    checkErrors() {
        let error = null;
        if (this.props.channelErrors && this.props.channelErrors.isError) {
            error = this.showErrorDialogue(this.props.channelErrors);
        } else if (this.props.contentErrors && this.props.contentErrors.isError) {
            error = this.showErrorDialogue(this.props.contentErrors);
        } else if (this.props.userSettingErrors && this.props.userSettingErrors.isError) {
            error = this.showErrorDialogue(this.props.userSettingErrors);
        }
        return error;
    }

    showErrorDialogue(errObject) {
        return (
            <MessageBanner
              show="true"
              type="error"
              errorData={errObject}
              errorHandled={this.errorMessageHandled.bind(this)}
              t={this.props.t}
            />
        );
    }

    errorMessageHandled(dataCall, errorCode) {
        const reloadCalls = ['putChannelTitle', 'putRecordingTitle', 'putChannelDescription', 'putChannelGroup'];
        if (errorCode === 401 || reloadCalls.includes(dataCall)) {
            window.location.reload();
        }
        if (this.props.channelErrors && this.props.channelErrors.isError) {
            this.props.channelProfileErrorHandled();
        }
        if (this.props.contentErrors && this.props.contentErrors.isError) {
            this.props.contentErrorHandled();
        }
        if (this.props.userSettingErrors && this.props.userSettingErrors.isError) {
            this.props.privateSettingErrorHandled();
        }
    }

    // ------------------------------------------- SEO Meta Tags for Prerender.io ------------------------------------------------
    setMetaTags() {
        if (this.props.channel) {
            const {t} = this.props;
            const channel = this.props.channel;
            return (
                <Helmet>
                    {channel.channelTitle ?
                        <title>{`${channel.channelTitle} | GoToStage.com `}</title>
                        : ''
                    }
                    {channel.channelDescription ?
                        <meta name="description" content={channel.channelDescription.replace('"', /g/)} />
                        : ''
                    }
                    {channel.isLimitedAudience ?
                        <meta name="robots" content="noindex" />
                        : ''
                    }

                    <meta name="keywords" content={t('metaTags.keywords')} />
                    <meta property="og:image" content={channel.channelLogoCdn || channel.channelBannerCdn || config.metaTags.thumbnailUrl} />
                </Helmet>
            );
        }
        return ('');
    }

    // ---------------------------------------- Terms of Service Modal ------------------------------------------------
    showTermsOfServiceBanner() {
        const props = this.props;
        const termsOfService = this.props.termsOfService;

        return props.isChannelOwner && termsOfService && termsOfService.statusLoaded && !termsOfService.requiredVersionAccepted
            ? <TOSBanner t={this.props.t} />
            : '';
    }

    // ------------------------------------------- DISPLAY CHANNEL INFO ------------------------------------------------
    displayChannelInfo() {
        const { channel, isChannelOwner, channelKey, isContentDiscoverable } = this.props;
        return (
            <ChannelInfo
              isChannelOwner={isChannelOwner}
              title={(channel && channel.channelTitle) ? channel.channelTitle : null}
              logo={(channel && channel.channelLogoCdn && channel.channelLogo) ? channel.channelLogoCdn : null}
              description={channel && channel.channelDescription ? channel.channelDescription : null}
              linkValue={(channelKey) ? `${config.app.baseUrl}/channel/${channelKey}` : null}
              isContentDiscoverable={isContentDiscoverable}
              isLimitedAudience={channel && channel.isLimitedAudience ? channel.isLimitedAudience : null}
              onChannelTitleChange={this.channelTitleChanged.bind(this)}
              onChannelDescriptionChange={this.channelDescriptionChange.bind(this)}
              onLogoAddClick={this.handleLogoAdd.bind(this)}
              onLogoDeleteClick={this.handleLogoDelete.bind(this)}
              onPrivacyChange={this.handlePrivacyChange.bind(this)}
              onNotificationChange={this.handleNotificationDisplay.bind(this)}
              okBtn={this.setTooltipState.bind(this, '.share-channel-tooltip', 'shownChannelLogoTooltip', { showChannelLogoTooltip: false})}
            />
        );
    }

    channelTitleChanged(id, data) {
        mixpanelTracker.track(`${mixpanelTracker.channelPage}-${mixpanelTracker.titleChanged}`);
        this.props.putChannelTitle(this.props.channelKey, data);
    }

    channelDescriptionChange(data) {
        mixpanelTracker.track(`${mixpanelTracker.channelPage}-${mixpanelTracker.descriptionChanged}`);
        this.props.putChannelDescription(this.props.channel.channelKey, data);
    }

    handleLogoDelete() {
        mixpanelTracker.track(`${mixpanelTracker.channelPage}-${mixpanelTracker.logoRemoveClicked}`);
        this.props.putChannelLogo(this.props.channel.channelKey, false);
    }

    handleLogoAdd() {
        mixpanelTracker.track(`${mixpanelTracker.channelPage}-${mixpanelTracker.logoUploadClicked}`);
        this.openFilePickerForChannelLogo();
    }

    openFilePickerForChannelLogo() {
        const self = this;
        const filePickerOptions = {
            fileType: 'logos',
            mimetype: 'image/*',
            cropRatio: 1 / 1,
            imageMin: [160, 160],
            customText: config.filepicker.logo.customText,
            customCss: config.filepicker.logo.customCss
        };

        filePickerHelper.pickAndStore(filePickerOptions, (channelLogo) => {
            self.props.putChannelLogo(self.props.channel.channelKey, channelLogo);
            mixpanelTracker.track(`${mixpanelTracker.channelPage}-${mixpanelTracker.logoImageUploaded}`);
        });
    }

    handlePrivacyChange(type) {
        this.props.setUserSettings({isContentDiscoverable: type})
        .then(() => {
            this.props.setLimitedAudience(!type);
        });
    }
    handleNotificationDisplay(successNotificationObject) {
        this.props.initNotification(successNotificationObject);
    }

    // ------------------------------------------- DISPLAY CONTENTS ------------------------------------------------
    displayContents() {
        const {
            isChannelOwner, channel, allGroupContentFetched, contents
        } = this.props;
        if (allGroupContentFetched && (!contents || contents.length === 0)) {
            return this.displayNoContents();
        }
        const allContents = [];
        allContents.push(this.displayFeaturedContents());
        if (channel && channel.defaultGroupKey && this.props.groupData) {
            allContents.push(this.displayGroupedContent(channel.defaultGroupKey,
                 groupsHelper.getDefaultGroupData(this.props.groupData, channel.defaultGroupKey)));
        }
        if (isChannelOwner) {
            allContents.push(this.displayCreateGroupIcon());
        }
        if (channel && channel.defaultGroupKey && this.props.groupData) {
            allContents.push(this.displayGroupedContent(channel.groupKeys,
                 groupsHelper.getGroupDataWithoutDefaultGroup(this.props.groupData, channel.defaultGroupKey)));
        }
        return allContents;
    }

    displayNoContents() {
        const {t} = this.props;
        const noContentstring = (this.props.isChannelOwner) ? t('channel.content.norecording') : t('channel.content.noRecordingAttendee');
        return (
            <div className={`${classPrefix}-no-contents`}>
                <img id="no-content-image" src={`${config.assets.url}/images/catalog/no-content_thumb.png`} alt="" />
                <span className="norecordings">{noContentstring}</span>
            </div>
        );
    }

    displayFeaturedContents() {
        const {t} = this.props;
        const self = this;
        let showEmptyFeatured;
        try {
            showEmptyFeatured = sessionStorage.getItem('showEmptyFeatured');
            (showEmptyFeatured === null) ? sessionStorage.setItem('showEmptyFeatured', true) : null;
        } catch (e) {
            console.log('error', e);
        }
        const featuredContents = contentsHelper.contentsFeatured(this.props.contents, this.props.featured);
        if (featuredContents && featuredContents.length > 0) {
            return (
                <CarouselFeatured
                  key="Featured"
                  title={t('channel.featured.title')}
                  details={self.createCatalogCard(featuredContents, true, true)}
                  carouselType="featuredCarousel"
                />
            );
        }
        return (this.props.isChannelOwner && ((showEmptyFeatured === 'true') || showEmptyFeatured === null)) ? <NoFeaturedEvent t={this.props.t} /> : null;
    }

    // ------------------------------------------- DISPLAY GROUPS ------------------------------------------------

    displayCreateGroupIcon() {
        return (
            <CreateGroupPopover
              t={this.props.t}
              groupData={this.createNewGroup.bind(this)}
            />
        );
    }

    createNewGroup(groupName) {
        mixpanelTracker.track(`${mixpanelTracker.channelPage}-${mixpanelTracker.newGroupCreated}`);
        this.props.createGroup(this.props.channel.channelKey, groupName);
    }


    displayGroupedContent(groupKeys, groupData) {
        const {t, channel} = this.props;
        return (groupKeys) ?
            <Groups
              t={t}
              key={groupKeys}
              isChannelOwner={this.props.isChannelOwner}
              channelKey={channel.channelKey}
              groupData={groupData}
              deleteGroup={this.props.deleteGroup}
              displayContent={this.createCatalogCard.bind(this)}
              editRecordingTitle={this.editRecordingTitle.bind(this)}
              deleteThumbnail={this.deleteThumbnail}
              featuredClicked={this.featuredClicked.bind(this)}
              isFeatured={this.isFeatured.bind(this)}
              thumbnailUploadClicked={this.thumbnailUploadClicked.bind(this)}
              categoryChangeClicked={this.categoryChangeClicked.bind(this)}
              mountInternalErrorPage={this.mountInternalErrorPage.bind(this)}
              numberOfGroups={groupsHelper.getAllGroupKeys(channel).length}
            />
            : null;
    }

    createCatalogCard(contents, isUncategorized, isFeatured) {
        const contentArray = [];
        const self = this;

        contents.forEach((content, key) => {
            contentArray.push(<div
              className={`catalog-card-wrapper ${isFeatured ? 'featured' : ''}`}
            >
                <CatalogCards
                  key={key}
                  details={content}
                  index={content.id}
                  isChannelOwner={self.props.isChannelOwner}
                  isFeatured={self.isFeatured(content.id)}
                  thumbnailUploadClicked={self.thumbnailUploadClicked.bind(this)}
                  editRecordingTitle={self.editRecordingTitle.bind(this)}
                  channelTitle={(self.props.channel && self.props.channel.channelTitle) ? self.props.channel.channelTitle : ''}
                  categoryChangeClicked={self.categoryChangeClicked.bind(this)}
                  featuredClicked={self.featuredClicked.bind(this)}
                  featureDisable={self.props.featureDisable}
                  params={this.props.params.user}
                  deleteThumbnail={this.deleteThumbnail.bind(this)}
                />
            </div>);
        });

        return contentArray;
    }

    isFeatured(contentKey) {
        return (this.props.featured && this.props.featured.includes(contentKey));
    }

    thumbnailUploadClicked(id, body) {
        this.props.createThumbnail(id, body);
    }

    deleteThumbnail(id, e) {
        e.stopPropagation();
        this.props.deleteThumbnail(id);
    }

    editRecordingTitle(id, body) {
        this.props.editRecordingTitle(id, body);
    }

    categoryChangeClicked(id, body) {
        this.props.createCategory(id, body);
    }

    featuredClicked(id, body) {
        this.props.putFeaturedContent(this.props.channel.channelKey, id, body);
    }

    // ------------------------------------------- RENDER ------------------------------------------------
    render() {
        return (
            <section className="body">
                {this.displayChannelInfo()}
                {this.setMetaTags()}
                <div id={`${classPrefix}`} className={`${classPrefix}`}>
                    {this.checkErrors()}
                    <div className={`${classPrefix}-contents`}>
                        {this.displayContents()}
                        <div style={{clear: 'both'}} />
                    </div>
                </div>
                {this.showTermsOfServiceBanner()}
            </section>
        );
    }
}

Catalog.propTypes = {
    auth: PropTypes.object,
    identity: PropTypes.object,
    isChannelOwner: PropTypes.bool,
    termsOfService: PropTypes.object,
    channelKey: PropTypes.string,
    channel: PropTypes.object,
    channelProfileFetching: PropTypes.number,
    putChannelLogo: PropTypes.func,
    channelContentIds: PropTypes.array,
    getAllContents: PropTypes.func,
    contents: PropTypes.array,
    setClassNameFooter: PropTypes.func,
    setRightFooter: PropTypes.func,
    setClassNameHeader: PropTypes.func,
    getContentIds: PropTypes.func,
    getFeaturedContentList: PropTypes.func,
    getGroupData: PropTypes.func,
    putChannelBanner: PropTypes.func,
    initHeader: PropTypes.func,
    initFooter: PropTypes.func,
    t: PropTypes.func,
    putChannelTitle: PropTypes.func,
    channelErrors: PropTypes.object,
    contentErrorHandled: PropTypes.func,
    contentErrors: PropTypes.object,
    channelProfileErrorHandled: PropTypes.func,
    featured: PropTypes.array,
    params: PropTypes.object,
    createThumbnail: PropTypes.func,
    deleteThumbnail: PropTypes.func,
    editRecordingTitle: PropTypes.func,
    createCategory: PropTypes.func,
    putFeaturedContent: PropTypes.func,
    putChannelDescription: PropTypes.func,
    createGroup: PropTypes.func,
    deleteGroup: PropTypes.func,
    groupData: PropTypes.array,
    contentsFetching: PropTypes.number,
    allGroupContentFetched: PropTypes.bool,
    getUserSettings: PropTypes.func,
    setUserSettings: PropTypes.func,
    initNotification: PropTypes.func,
    isContentDiscoverable: PropTypes.bool,
    userSettingErrors: PropTypes.object,
    privateSettingErrorHandled: PropTypes.func,
    setLimitedAudience: PropTypes.func
};

export default connect(
    state => ({
        contents: state.contents.data,
        contentsFetching: state.contents.isFetching,
        allGroupContentFetched: state.contents.allGroupContentFetched,
        channels: state.channel.channels,
        channel: state.channel.channel,
        channelProfileFetching: state.channel.isFetching,
        channelContentIds: state.channel.channelContentIds,
        featured: state.channel.featured,
        featureDisable: state.channel.featureDisable,
        channelErrors: state.channel.error,
        contentErrors: state.contents.error,
        groupData: state.channel.groupData,
        termsOfService: state.termsOfService,
        isContentDiscoverable: state.userSetting.isContentDiscoverable,
        userSettingErrors: state.userSetting.error
    }),
    dispatch => bindActionCreators(Object.assign({}, contentsActions, channelsActions, HeaderActions, FooterActions, NotificationActions, UserSettingActions), dispatch)
)(translate(['translation'])(Catalog));



// WEBPACK FOOTER //
// ./src/components/catalog/catalog.js